import { useState } from "react"
import { Input } from "@/components/ui/input"
import { Button } from "@/components/ui/button"
import {
  Field,
  FieldGroup,
  FieldLabel,
  FieldDescription,
} from "@/components/ui/field"
import { SearchableCombobox } from "@/components/ui/searchable-combobox"
import { useProjectSearch } from "@/features/projects/queries/projects-queries"
import { useDebounce } from "@/lib/hooks/use-debounce"

export function ManifestAmountField({
  amount,
  onChange,
}: {
  amount: string
  onChange: (value: string) => void
}) {
  return (
    <Field>
      <FieldLabel htmlFor="new-lump-sum">Lump sum before tax ($)</FieldLabel>
      <Input
        id="new-lump-sum"
        type="number"
        min="0"
        max="21474836.47"
        step="0.01"
        value={amount}
        onChange={(e) => onChange(e.target.value)}
      />
      <FieldDescription>
        Optional while drafting. Required before production release.
      </FieldDescription>
    </Field>
  )
}

export function ManifestCreateFields({
  amount,
  setAmount,
  projectId,
  setProjectId,
}: {
  amount: string
  setAmount: (value: string) => void
  projectId: number | null
  setProjectId: (id: number | null) => void
}) {
  const [search, setSearch] = useState("")
  const [label, setLabel] = useState("")
  const debounced = useDebounce(search, 200)
  const { data, isLoading } = useProjectSearch(debounced)
  const projects = data?.results ?? []
  return (
    <FieldGroup>
      <ManifestAmountField amount={amount} onChange={setAmount} />
      <Field>
        <div className="flex items-center justify-between">
          <FieldLabel>Project (optional)</FieldLabel>
          {projectId && (
            <Button
              variant="ghost"
              type="button"
              size="sm"
              onClick={() => {
                setProjectId(null)
                setLabel("")
              }}
            >
              Clear
            </Button>
          )}
        </div>
        <SearchableCombobox
          value={projectId}
          onChange={(id) => {
            const next = id == null ? null : Number(id)
            setProjectId(next)
            setLabel(projects.find((p) => p.id === next)?.name ?? "")
          }}
          options={projects}
          getOptionValue={(p) => p.id}
          getOptionLabel={(p) => p.name}
          fallbackLabel={label}
          searchValue={search}
          onSearchChange={setSearch}
          loading={isLoading}
          placeholder="Standalone order, or select a project"
        />
        <FieldDescription>
          After creation, import an ORDX file or add rooms by hand. Project
          billing stays with the project.
        </FieldDescription>
      </Field>
    </FieldGroup>
  )
}
